import {
  DISPOSITION_AI_RESOLVED,
  DISPOSITION_DOCS_NOT_ENOUGH,
  DISPOSITION_OTHER,
  DISPOSITION_SOLVED,
  DISPOSITION_UNATTENDED,
  DOC_THUMB_ID,
  DOC_THUMB_SSN,
  DOC_THUMB_UB,
  DOCUMENT_NOUN_MANY,
  DOCUMENT_NOUN_ONE,
  RESOLVE_FLAG,
  RESOLVE_FLAG_AI,
  AI_NOTE,
  AI_NOTE_ACTOR_FALLBACK,
  NOTE_EARLIER_RESOLVED,
  NOTE_WAITING,
  SUBMITTED_PREFIX,
  TAGGED_BY,
  TAGGED_LABEL,
} from '../constants';
import { formatBannerDateTime } from '../StateBanner/utils';
import type { IdvAttempt, IdvDocument, IdvResolution } from '../types';
import type { AttemptCardView, DispositionBadgeView, DocChipView, MarkAsVariant } from './types';

const sameId = (a: string | number | null | undefined, b: string | number | null | undefined): boolean =>
  a != null && b != null && String(a) === String(b);

export const isAiResolvedAttempt = (attempt: IdvAttempt, resolution: IdvResolution): boolean =>
  resolution.resolved_kind === 'ai' && sameId(attempt.id, resolution.resolved_attempt_id);

export const getDocumentCountLabel = (count: number): string =>
  `${count} ${count === 1 ? DOCUMENT_NOUN_ONE : DOCUMENT_NOUN_MANY}`;

export const getDocumentTypesLabel = (documents: IdvDocument[]): string | null => {
  const types = documents.map((document) => document.type.trim()).filter(Boolean);
  return types.length > 0 ? types.join(' + ') : null;
};

export const getDocumentSummary = (documents: IdvDocument[]): string => {
  const countLabel = getDocumentCountLabel(documents.length);
  const types = getDocumentTypesLabel(documents);
  return types ? `${countLabel} · ${types}` : countLabel;
};

export const getDocThumb = (document: IdvDocument): string => {
  const code = document.type_code?.trim();
  if (code) return code.slice(0, 4).toUpperCase();

  const type = document.type.toLowerCase();
  if (type.includes('ssn') || type.includes('social')) return DOC_THUMB_SSN;
  if (type.includes('utilit')) return DOC_THUMB_UB;
  if (type.includes('license') || type.includes('passport') || /\bid\b/.test(type)) return DOC_THUMB_ID;

  const letters = document.type
    .replace(/[^a-zA-Z]/g, '')
    .slice(0, 3)
    .toUpperCase();
  return letters || DOC_THUMB_ID;
};

export const getDocChipView = (document: IdvDocument): DocChipView => ({
  key: String(document.id),
  thumb: getDocThumb(document),
  label: document.type.trim() || document.type,
  idLabel: `#${document.id}`,
  extension: document.extension.trim().toUpperCase(),
  href: document.open_url?.trim() || null,
});

export const getDispositionBadge = (attempt: IdvAttempt, resolution: IdvResolution): DispositionBadgeView => {
  if (isAiResolvedAttempt(attempt, resolution)) {
    return { label: DISPOSITION_AI_RESOLVED, tone: 'success' };
  }
  if (attempt.disposition === 'solved') return { label: DISPOSITION_SOLVED, tone: 'success' };
  if (attempt.disposition === 'docs_not_enough') return { label: DISPOSITION_DOCS_NOT_ENOUGH, tone: 'warning' };
  if (attempt.disposition === 'other') return { label: DISPOSITION_OTHER, tone: 'info' };
  return { label: DISPOSITION_UNATTENDED, tone: 'warning' };
};

export const getResolveFlagLabel = (attempt: IdvAttempt, resolution: IdvResolution): string | null => {
  if (!attempt.resolves_case) return null;
  return isAiResolvedAttempt(attempt, resolution) ? RESOLVE_FLAG_AI : RESOLVE_FLAG;
};

export const getMarkAsVariant = (attempt: IdvAttempt, resolution: IdvResolution): MarkAsVariant => {
  const unattended = attempt.disposition == null && !isAiResolvedAttempt(attempt, resolution);
  return unattended ? 'primary' : 'secondary';
};

export const formatAttemptTagDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return dateStr;
  const day = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${day}, ${time}`;
};

const taggedNote = (attempt: IdvAttempt): string | null => {
  const comment = attempt.disposition_note?.trim() || attempt.disposition_comment?.trim() || '';
  const name = attempt.disposition_by?.name?.trim() || '';
  const taggedAt = attempt.disposition_at ? formatAttemptTagDate(attempt.disposition_at) : '';
  if (attempt.disposition == null && !comment && !name && !taggedAt) return null;

  const head = [name ? TAGGED_BY(name) : taggedAt ? TAGGED_LABEL : '', taggedAt].filter(Boolean).join(' · ');
  if (head && comment) return `${head} — “${comment}”`;
  return head || comment || null;
};

const earlierAttemptResolved = (attempt: IdvAttempt, resolution: IdvResolution, attempts: IdvAttempt[]): boolean => {
  if (resolution.resolved_attempt_id == null || sameId(resolution.resolved_attempt_id, attempt.id)) return false;
  const resolver = attempts.find((item) => sameId(item.id, resolution.resolved_attempt_id));
  if (!resolver?.submitted_at || !attempt.submitted_at) return true;
  return new Date(resolver.submitted_at).getTime() < new Date(attempt.submitted_at).getTime();
};

export const getAttemptNote = (attempt: IdvAttempt, resolution: IdvResolution, attempts: IdvAttempt[] = []): string => {
  if (isAiResolvedAttempt(attempt, resolution)) {
    const actor = resolution.resolved_by?.trim() || AI_NOTE_ACTOR_FALLBACK;
    const when = resolution.resolved_at ? formatAttemptTagDate(resolution.resolved_at) : '';
    return AI_NOTE(actor, when);
  }

  return (
    taggedNote(attempt) ??
    (earlierAttemptResolved(attempt, resolution, attempts) ? NOTE_EARLIER_RESOLVED : NOTE_WAITING)
  );
};

export const getAttemptCardView = (
  attempt: IdvAttempt,
  resolution: IdvResolution,
  attempts: IdvAttempt[] = []
): AttemptCardView => {
  const note = getAttemptNote(attempt, resolution, attempts);

  return {
    idLabel: `#${attempt.id}`,
    submittedLabel: `${SUBMITTED_PREFIX}${formatBannerDateTime(attempt.submitted_at)}`,
    summaryCount: getDocumentCountLabel(attempt.documents.length),
    summaryTypes: getDocumentTypesLabel(attempt.documents),
    aiBadge: attempt.ai_badge,
    aiSummary: attempt.ai_summary?.trim() || null,
    disposition: getDispositionBadge(attempt, resolution),
    resolveFlag: getResolveFlagLabel(attempt, resolution),
    resolving: attempt.resolves_case,
    markAsVariant: getMarkAsVariant(attempt, resolution),
    canMarkAs: attempt.can_mark_as,
    note,
  };
};
